import { useCallback, useEffect, useRef, useState } from 'react';
import type { MemoryFileCatalog } from '../../shared/memoryFile';
import { i18nService } from '../services/i18n';

/** 分页目录只负责查询；写入和全文历史继续由既有文件编辑器处理。 */
export function useMemoryFileCatalog(scope: 'secretary' | 'global', query: string) {
  const [catalog, setCatalog] = useState<MemoryFileCatalog | null>(null);
  const [busy, setBusy] = useState(false), [error, setError] = useState('');
  const generation = useRef(0), locked = useRef(false);
  const api = window.electron.cowork;
  const load = useCallback(async (offset = 0) => {
    if (offset && locked.current) return;
    // 保存后刷新必须替换正在进行的旧查询，不能因查询锁而漏掉刚完成的写入。
    if (!offset) generation.current++;
    const epoch = generation.current; locked.current = true; setBusy(true); setError('');
    try {
      const result = await api.listMemoryFiles?.(scope, query.trim(), offset);
      if (epoch !== generation.current) return;
      if (!result || !Array.isArray(result.items)) throw new Error(i18nService.t('memoryFile_unavailable'));
      setCatalog(old => ({ ...result, items: offset && old ? [...new Map([...old.items, ...result.items].map(row => [row.id, row])).values()] : result.items }));
    } catch (err) { if (epoch === generation.current) setError(err instanceof Error ? err.message : i18nService.t('memoryFile_unavailable')); }
    finally { if (epoch === generation.current) { locked.current = false; setBusy(false); } }
  }, [api, scope, query]);
  useEffect(() => {
    const timer = setTimeout(() => void load(), query ? 300 : 0);
    const clear = () => { clearTimeout(timer); generation.current++; locked.current = false; setCatalog(null); setError(''); setBusy(false); };
    generation.current++; locked.current = false; setCatalog(null); setError(''); setBusy(false);
    window.addEventListener('auth:identityChanging', clear);
    return () => { clearTimeout(timer); generation.current++; locked.current = false; window.removeEventListener('auth:identityChanging', clear); };
  }, [load, query]);
  return { catalog, busy, error, reload: () => load(), more: () => load(catalog?.nextOffset ?? 0) };
}
